import React, { useMemo } from 'react';
import EnhancedChart from './MultiChart';
import { Typography } from '@mui/material';

// Interfaces for wait time data
export interface IWaitTimeDistribution {
  withinWeek: number;
  withinTwoWeeks: number;
  withinMonth: number;
  overMonth: number;
}

export interface IAppointmentTypeWaitTimeStats {
  appointmentType: string;
  departmentUuid: string;
  departmentName: string;
  totalAppointments: number;
  avgWaitTime: number;
  minWaitTime: number;
  maxWaitTime: number;
  medianWaitTime: number;
  percentile90WaitTime: number;
  waitTimeDistribution: IWaitTimeDistribution;
  waitTimeDistributionPercentage: IWaitTimeDistribution;
}

export interface IAppointmentWaitTimeStats {
  status: number;
  waitTimeStats?: IAppointmentTypeWaitTimeStats[];
  errorMessage?: string;
}

interface AppointmentWaitTimeDistributionChartProps {
  data: IAppointmentWaitTimeStats;
  title?: string;
  chartType?: 'bar' | 'pie' | 'donut';
  horizontal?: boolean;
  usePercentage?: boolean;
}

const AppointmentWaitTimeDistributionChart = ({
  data,
  title = "Wait Time Distribution by Department",
  chartType = 'bar',
  horizontal = true,
  usePercentage = true
}: AppointmentWaitTimeDistributionChartProps) => {
  
  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!data.waitTimeStats || data.waitTimeStats.length === 0) {
      return {
        series: [],
        departments: [],
        categories: []
      };
    }
    
    // Get unique departments and appointment types
    const departments = data.waitTimeStats && Array.isArray(data.waitTimeStats) 
      ? [...new Set(data.waitTimeStats.map(item => item.departmentName))].sort()
      : [];
    const appointmentTypes = data.waitTimeStats && Array.isArray(data.waitTimeStats)
      ? [...new Set(data.waitTimeStats.map(item => item.appointmentType))].sort()
      : [];
    
    // Categories for the wait time distribution
    const categories = ['Within 1 Week', 'Within 2 Weeks', 'Within 1 Month', 'Over 1 Month'];
    
    // Create series data for each department (potentially filtered by appointment type)
    const series = departments.map(dept => {
      // For each department, we'll aggregate the distribution data
      const deptItems = data.waitTimeStats?.filter(item => item.departmentName === dept);
      
      if (!deptItems || deptItems.length === 0) {
        return {
          name: dept,
          data: [0, 0, 0, 0]
        };
      }
      
      // Choose between raw numbers or percentage
      const distributionField = usePercentage ? 'waitTimeDistributionPercentage' : 'waitTimeDistribution';
      
      // Aggregate distribution data
      const aggregatedDistribution = deptItems.reduce(
        (agg, item) => {
          const dist = item[distributionField];
          return {
            withinWeek: agg.withinWeek + dist.withinWeek,
            withinTwoWeeks: agg.withinTwoWeeks + dist.withinTwoWeeks,
            withinMonth: agg.withinMonth + dist.withinMonth,
            overMonth: agg.overMonth + dist.overMonth
          };
        },
        { withinWeek: 0, withinTwoWeeks: 0, withinMonth: 0, overMonth: 0 }
      );
      
      // If using percentages and there are multiple appointment types per department,
      // we need to normalize the percentages
      if (usePercentage && deptItems.length > 1) {
        const total = deptItems.reduce((sum, item) => sum + item.totalAppointments, 0);
        const normalizedDistribution = {
          withinWeek: Math.round(deptItems.reduce((sum, item) => 
            sum + (item.waitTimeDistributionPercentage.withinWeek * item.totalAppointments / total), 0)),
          withinTwoWeeks: Math.round(deptItems.reduce((sum, item) => 
            sum + (item.waitTimeDistributionPercentage.withinTwoWeeks * item.totalAppointments / total), 0)),
          withinMonth: Math.round(deptItems.reduce((sum, item) => 
            sum + (item.waitTimeDistributionPercentage.withinMonth * item.totalAppointments / total), 0)),
          overMonth: Math.round(deptItems.reduce((sum, item) => 
            sum + (item.waitTimeDistributionPercentage.overMonth * item.totalAppointments / total), 0))
        };
        
        return {
          name: dept,
          data: [
            normalizedDistribution.withinWeek,
            normalizedDistribution.withinTwoWeeks,
            normalizedDistribution.withinMonth,
            normalizedDistribution.overMonth
          ]
        };
      }
      
      return {
        name: dept,
        data: [
          aggregatedDistribution.withinWeek,
          aggregatedDistribution.withinTwoWeeks,
          aggregatedDistribution.withinMonth,
          aggregatedDistribution.overMonth
        ]
      };
    });
    
    return {
      series,
      departments,
      categories
    };
  }, [data, usePercentage]);
  
  // For pie/donut charts, we need to transform the data
  const pieChartData = useMemo(() => {
    if (!['pie', 'donut'].includes(chartType) || !data?.waitTimeStats || !Array.isArray(data.waitTimeStats) || data.waitTimeStats.length === 0) {
      return null;
    }
    
    // For pie charts, we'll look at the overall distribution across all departments
    const distributionField = usePercentage ? 'waitTimeDistributionPercentage' : 'waitTimeDistribution';
    
    const totalAppointments = data.waitTimeStats.reduce((sum, item) => sum + (item?.totalAppointments || 0), 0);
    
    // Calculate overall distribution
    const overallDistribution = {
      withinWeek: 0,
      withinTwoWeeks: 0,
      withinMonth: 0,
      overMonth: 0
    };
    
    data.waitTimeStats.forEach(item => {
      const weight = item.totalAppointments / totalAppointments;
      const dist = item[distributionField];
      
      overallDistribution.withinWeek += usePercentage ? dist.withinWeek * weight : dist.withinWeek;
      overallDistribution.withinTwoWeeks += usePercentage ? dist.withinTwoWeeks * weight : dist.withinTwoWeeks;
      overallDistribution.withinMonth += usePercentage ? dist.withinMonth * weight : dist.withinMonth;
      overallDistribution.overMonth += usePercentage ? dist.overMonth * weight : dist.overMonth;
    });
    
    // If not using percentages, we need to normalize the data for the pie chart
    if (!usePercentage) {
      const total = Object.values(overallDistribution).reduce((a, b) => a + b, 0);
      Object.keys(overallDistribution).forEach(key => {
        overallDistribution[key as keyof IWaitTimeDistribution] = 
          Math.round(overallDistribution[key as keyof IWaitTimeDistribution] / total * 100);
      });
    }
    
    const series = [
      {
        name: 'Within 1 Week',
        data: [Math.round(overallDistribution.withinWeek)]
      },
      {
        name: 'Within 2 Weeks',
        data: [Math.round(overallDistribution.withinTwoWeeks)]
      },
      {
        name: 'Within 1 Month',
        data: [Math.round(overallDistribution.withinMonth)]
      },
      {
        name: 'Over 1 Month',
        data: [Math.round(overallDistribution.overMonth)]
      }
    ];
    
    return {
      series,
      categories: ['All Departments'],
      total: 100
    };
  }, [data, chartType, usePercentage]);
  
  // Format title component
  const titleComponent = (
    <Typography variant="h6">
      {title} {usePercentage ? '(%)' : ''}
    </Typography>
  );
  
  // Choose the right data based on chart type
  const chartProps = ['pie', 'donut'].includes(chartType) && pieChartData
    ? {
        series: pieChartData.series,
        title: titleComponent,
        categories: pieChartData.categories,
        type: chartType,
        total: pieChartData.total
      }
    : {
        series: chartData.series,
        title: titleComponent,
        categories: chartData.categories,
        type: 'bar' as 'bar' | 'line' | 'area' | 'radar' | 'scatter' | 'heatmap' | 'pie' | 'donut',
        stacked: false,
        horizontal,
        
      };

  return <EnhancedChart {...chartProps} />;
};

export default AppointmentWaitTimeDistributionChart;